import type { ThriveAppEventBus } from '../../platform/runtime/app-events';
import type { KeyboardFreezeGate } from './keyboard-adapter';

/**
 * The sitting's keyboard controls: freeze, repeat, pause (game design spec §30, D-045 PS2-5).
 *
 * These were Hero 1's keys through Phase 1 because Hero 1 was the only hero. With two children at
 * one keyboard they cannot stay there: two adapters both binding F would toggle the freeze twice
 * per press and cancel it out, and a freeze that only silenced one child would not be a stillness
 * test at all. So one owner binds them for the whole stage.
 *
 *   F   freeze — suppresses every emission on the stage, for every hero, until pressed again
 *   R   ask the shell to say the instruction again
 *   P   ask the shell to pause
 *
 * R and P are shell concerns rather than player actions, so they travel on the React↔Phaser app
 * bus and never on the semantic input bus (D-035): nothing downstream should mistake "a grown-up
 * asked for a pause" for something a child's body did.
 *
 * The freeze itself is exposed as a gate the adapters read rather than a flag they each keep, so
 * there is exactly one frozen state on the stage and no way for the two heroes to disagree about
 * it. Lifting the freeze notifies them, which is how a posture held into a freeze — whose keyup
 * arrived while everything was deaf — gets retracted instead of latching for ever.
 */

const FREEZE_KEY = 'KeyF';
const REPEAT_KEY = 'KeyR';
const PAUSE_KEY = 'KeyP';

const SESSION_CONTROL_KEYS: readonly string[] = [FREEZE_KEY, REPEAT_KEY, PAUSE_KEY];

/** Why the shell is being asked, so a log can tell a simulated request from a real one. */
const SIMULATION_REASON = 'keyboard-simulation';

export interface KeyboardSessionControlsOptions {
  /** Defaults to `window`; injected in tests. */
  target?: EventTarget | null;
  /**
   * Where the shell-level requests (R, P) go. Without it those keys are still consumed — so they
   * cannot scroll the page or type into anything — but ask for nothing.
   */
  appEvents?: ThriveAppEventBus;
}

export interface KeyboardSessionControls {
  attach(): void;
  detach(): void;
  readonly attached: boolean;
  /** Keys these controls consume, for the shell's on-screen help. */
  readonly boundKeys: readonly string[];
  /** The stage's single freeze, handed to every hero's adapter. */
  readonly freeze: KeyboardFreezeGate;
}

export function createKeyboardSessionControls(
  options: KeyboardSessionControlsOptions = {},
): KeyboardSessionControls {
  const { appEvents } = options;

  const target =
    options.target === undefined ? (typeof window === 'undefined' ? null : window) : options.target;

  let attached = false;
  let frozen = false;
  const listeners = new Set<(frozen: boolean) => void>();

  const setFrozen = (next: boolean): void => {
    if (next === frozen) return;
    frozen = next;
    for (const listener of [...listeners]) listener(frozen);
  };

  // The same rule the adapters use: a grown-up's on-screen control keeps the keyboard, so P must
  // not fire from a keypress aimed at a focused button (D-040(2)).
  const isEditingOrPressingControl = (event: KeyboardEvent): boolean => {
    const eventTarget = event.target;
    if (
      eventTarget === null ||
      !(typeof HTMLElement !== 'undefined' && eventTarget instanceof HTMLElement)
    ) {
      return false;
    }
    if (eventTarget.isContentEditable) return true;
    return ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'A'].includes(eventTarget.tagName);
  };

  const handleKeyDown = (event: Event): void => {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.repeat) return;
    if (isEditingOrPressingControl(keyboardEvent)) return;
    if (!SESSION_CONTROL_KEYS.includes(keyboardEvent.code)) return;

    keyboardEvent.preventDefault();

    if (keyboardEvent.code === FREEZE_KEY) {
      setFrozen(!frozen);
      return;
    }

    // A frozen stage is silent all the way through, including its shell requests.
    if (frozen) return;

    if (keyboardEvent.code === REPEAT_KEY) {
      appEvents?.emit('shell:repeat-narration-requested', { reason: SIMULATION_REASON });
      return;
    }

    // A request, not a state change: the shell owns whether the game is paused.
    appEvents?.emit('shell:pause-requested', { reason: SIMULATION_REASON });
  };

  return {
    get attached() {
      return attached;
    },

    boundKeys: [...SESSION_CONTROL_KEYS],

    freeze: {
      get frozen() {
        return frozen;
      },
      subscribe(listener: (next: boolean) => void): () => void {
        listeners.add(listener);
        return () => {
          listeners.delete(listener);
        };
      },
    },

    attach(): void {
      if (attached || target === null) return;
      attached = true;
      target.addEventListener('keydown', handleKeyDown);
    },

    detach(): void {
      if (!attached || target === null) return;
      attached = false;
      target.removeEventListener('keydown', handleKeyDown);
      // The controls are going away, so "frozen until somebody presses F" would mean "frozen for
      // ever" for any adapter still listening — and anything they hold would never be retracted.
      setFrozen(false);
    },
  };
}
